<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';

import { useAuthStore } from '@/stores/authStore';
import { useImageStore } from '@/stores/imageStore';
import { withImageVersion } from '@/utils/imageCdn';

// The organization logo tile with its "Change logo" action. The upload itself is unchanged: a
// hidden file input hands the chosen file to imageStore.createOrganizationImage and the preview is
// re-versioned so the browser refetches the replaced image. The default slot renders between the
// tile and the button so the profile modal can place the org identity there, as the design does.

const authStore = useAuthStore();
const imageStore = useImageStore();

const { currentOrg, logoUrl } = storeToRefs(authStore);
const { orgImageLoading } = storeToRefs(imageStore);

const fileInput = ref<HTMLInputElement>();
const logoImageFail = ref(false);

onMounted(() => {
    authStore.getLogoUrl();
});

const chooseFile = () => {
    fileInput.value?.click();
};

const onFileChanged = async (e: Event) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    try {
        const newLogoUrl = await imageStore.createOrganizationImage(file);
        if (newLogoUrl) {
            // Version the preview so the browser refetches the replaced logo instead of
            // serving its cached copy of the old one.
            logoUrl.value = withImageVersion(newLogoUrl, Date.now());
        } else {
            // Fallback: fetch logo URL from API if not returned in upload response
            await authStore.getLogoUrl();
        }
        logoImageFail.value = false;
    } catch (error) {
        console.log(error);
    } finally {
        // Let the same file be picked again after a failed or replaced upload.
        target.value = '';
    }
};

const setLogoImageFail = () => {
    logoImageFail.value = true;
};
</script>

<template>
    <div :class="$style.tile">
        <ProgressSpinner v-if="orgImageLoading" :class="$style.spinner" />
        <img
            v-else-if="logoUrl && !logoImageFail"
            :class="$style.image"
            :src="logoUrl"
            :alt="currentOrg ? `${currentOrg} logo` : 'Organization logo'"
            @error="setLogoImageFail"
        />
        <i v-else :class="$style.placeholder" class="pi pi-image" aria-hidden="true"></i>
    </div>

    <slot />

    <Button
        :label="logoUrl && !logoImageFail ? 'Change logo' : 'Add logo'"
        outlined
        severity="secondary"
        :class="$style.changeButton"
        :loading="orgImageLoading"
        @click="chooseFile"
    />
    <input ref="fileInput" type="file" :class="$style.fileInput" accept="image/*" capture @change="onFileChanged($event)" />

    <div :class="$style.hint">JPG, PNG or GIF<br />up to 512 × 512 px</div>
</template>

<style module>
.tile {
    width: 112px;
    height: 112px;
    box-sizing: border-box;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    flex-shrink: 0;
}

.image {
    width: 94px;
    height: 94px;
    object-fit: contain;
    border-radius: 4px;
}

.placeholder {
    font-size: 32px;
    color: #8a94a3;
}

.spinner {
    width: 40px;
    height: 40px;
}

.changeButton {
    height: 36px;
    padding: 0 14px;
    background: #ffffff;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    color: #1f2937;
    font-size: 13px;
    font-weight: 500;
}

.changeButton:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    color: #1f2937;
}

/* Kept in the DOM for the file picker, but never shown: the button above is the only trigger. */
.fileInput {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
}

.hint {
    font-size: 12px;
    line-height: 16px;
    color: #5f6b7a;
}
</style>
